import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Renders an accessible breadcrumb trail. Emits BreadcrumbList JSON-LD via
 * the parent page's useSeo() call — pass `items` to both this component
 * AND to the page's jsonLd.breadcrumb entry (use `breadcrumbJsonLd` helper).
 */
export default function Breadcrumbs({ items = [], className = "" }) {
  if (!items.length) return null;
  return (
    <nav data-testid="breadcrumbs" aria-label="Breadcrumb" className={`text-xs text-zinc-500 mb-6 ${className}`}>
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={`${item.label}-${i}`} className="flex items-center gap-1.5">
              {item.to && !isLast ? (
                <Link to={item.to} className="hover:text-white transition uppercase tracking-[0.18em]">
                  {item.label}
                </Link>
              ) : (
                <span className={`uppercase tracking-[0.18em] ${isLast ? "text-zinc-300" : ""}`} aria-current={isLast ? "page" : undefined}>
                  {item.label}
                </span>
              )}
              {!isLast && <ChevronRight size={10} className="text-zinc-700" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Build a BreadcrumbList JSON-LD object from the same items array. */
export function breadcrumbJsonLd(items, origin) {
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.label,
      ...(it.to ? { item: `${base}${it.to}` } : {}),
    })),
  };
}
